/**
 * CommunityPicker — Token + typeahead community selector.
 *
 * Matches the "Primary Location" selector pattern from ProjectConfigModal:
 *   - When a community is selected, it shows as a pill/token with a clear button
 *   - Typeahead search input filters communities with scored matching
 *   - Full community path display (e.g. "Boston, Massachusetts, United States")
 *   - Membership-aware: member communities sort first and show a badge
 *
 * Reads from the global data store (hydrated at app startup).
 */

import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { communityMembershipStore } from '../../Data/store';
import { normalizeGuid } from '../../utils/guid';
import { pathFromLeafGuid, pathDisplay } from '../../utils/entityPath';
import styles from './ProjectStepper.module.css';

/* ── Props ────────────────────────────────────────────────── */

interface CommunityPickerProps {
  currentUserGuid: string;
  value: string;          // selected community entity_guid
  displayValue: string;   // selected community display name (path string)
  onChange: (entityGuid: string, displayName: string) => void;
  /** All community entities (from App.tsx apiCommunityEntities) */
  communities: Array<{
    entity_guid?: string;
    entityGuid?: string;
    name: string;
    entity_type?: string;
    entityType?: string;
    is_active?: boolean;
    isActive?: boolean;
  }>;
  /** Community entity relationships (parent→child) for building path display */
  relationships?: Array<{
    parent_entity_guid?: string;
    child_entity_guid?: string;
    relationship_type?: string;
  }>;
}

/* ── Component ───────────────────────────────────────────── */

export default function CommunityPicker({
  currentUserGuid,
  value,
  displayValue,
  onChange,
  communities,
  relationships = [],
}: CommunityPickerProps) {
  const { t } = useAppTranslation('pages');
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // ── Derive membership set for current user ──────────────
  const memberGuids = useMemo(() => {
    const upperUser = currentUserGuid.toUpperCase();
    const set = new Set<string>();
    for (const m of communityMembershipStore) {
      if (
        String(m.user_guid || '').toUpperCase() === upperUser &&
        String(m.status || '').toLowerCase() === 'active'
      ) {
        set.add(String(m.community_entity_guid || '').toUpperCase());
      }
    }
    return set;
  }, [currentUserGuid]);

  // ── Build path display for a leaf guid ────────────────────
  const getPathDisplay = useCallback((leafGuid: string): string => {
    const g = normalizeGuid(leafGuid);
    if (!g) return '';
    // pathFromLeafGuid returns root→leaf; display prefers leaf→root
    const rootToLeaf = pathFromLeafGuid(g, communities as any, relationships as any);
    const parts = (rootToLeaf ?? []).map((p: any) => String(p?.name ?? '').trim()).filter(Boolean);
    return pathDisplay(parts.reverse());
  }, [communities, relationships]);

  // ── Build search index ────────────────────────────────────
  const communityIndex = useMemo(() => {
    const out: { guid: string; display: string; search: string; leafName: string; isMember: boolean }[] = [];
    if (!Array.isArray(communities)) return out;
    for (const e of communities) {
      const guid = normalizeGuid(e?.entity_guid || e?.entityGuid);
      if (!guid) continue;
      const isActive = e?.is_active ?? e?.isActive ?? true;
      if (!isActive) continue;
      const leafName = String(e?.name ?? '').trim();
      const display = getPathDisplay(guid);
      if (!display) continue;
      const search = `${leafName} ${display}`.toLowerCase();
      const isMember = memberGuids.has(guid.toUpperCase());
      out.push({ guid, display, search, leafName, isMember });
    }
    // Sort: member communities first, then alphabetical
    out.sort((a, b) => {
      if (a.isMember !== b.isMember) return a.isMember ? -1 : 1;
      return a.display.localeCompare(b.display);
    });
    return out;
  }, [communities, getPathDisplay, memberGuids]);

  // ── Filter suggestions by query ───────────────────────────
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as typeof communityIndex;

    const scored: (typeof communityIndex[number] & { score: number })[] = [];
    for (const row of communityIndex) {
      const name = row.leafName.toLowerCase();
      let score = -1;
      if (name.startsWith(q)) score = 100;
      else if (row.search.startsWith(q)) score = 90;
      else if (row.search.includes(` ${q}`)) score = 70;
      else if (row.search.includes(q)) score = 50;
      if (score < 0) continue;
      // Boost deeper paths (more specific communities)
      const depthBoost = Math.min(20, row.display.split(',').length);
      // Boost member communities
      const memberBoost = row.isMember ? 10 : 0;
      scored.push({ ...row, score: score + depthBoost + memberBoost });
    }
    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 12);
  }, [query, communityIndex]);

  // ── Resolve display for currently selected value ──────────
  const selectedDisplay = useMemo(() => {
    if (!value) return '';
    // If we already have a display value from parent, use it
    if (displayValue) return displayValue;
    return getPathDisplay(value);
  }, [value, displayValue, getPathDisplay]);

  // ── Close on outside click ──────────────────────────────
  useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggestions]);

  const handleSelect = useCallback((guid: string, display: string) => {
    onChange(guid, display);
    setQuery('');
    setShowSuggestions(false);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('', '');
    setQuery('');
    setShowSuggestions(false);
  }, [onChange]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Token input area */}
      <div className={styles.communityTokenInput}>
        {/* Selected token */}
        {value ? (
          <div className={styles.communityToken} title={selectedDisplay}>
            <span className={styles.communityTokenText}>{selectedDisplay}</span>
            <button
              type="button"
              className={styles.communityTokenClear}
              onClick={handleClear}
              aria-label={t('stepper.communityClearLabel')}
            >
              ×
            </button>
          </div>
        ) : null}

        {/* Search input */}
        <input
          className={styles.communityTokenField}
          value={query}
          placeholder={value ? t('stepper.communityChangeHint') : t('stepper.communityPickerPlaceholder')}
          onChange={(e) => {
            const v = e.target.value;
            // If a token is selected and user starts typing, clear the token
            if (value && v.trim().length > 0) {
              onChange('', '');
            }
            setQuery(v);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
        />
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 ? (
        <div className={styles.communitySuggestions}>
          {suggestions.map((s) => (
            <div
              key={s.guid}
              className={styles.communitySuggestionItem}
              onMouseDown={() => handleSelect(s.guid, s.display)}
            >
              <span>{s.display}</span>
              {s.isMember && (
                <span className={styles.communityMemberBadge}>{t('stepper.communityMemberBadge')}</span>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* "None" option — shown when user is typing but no selection */}
      {showSuggestions && query.trim() === '' && !value ? (
        <div className={styles.communitySuggestions}>
          <div
            className={styles.communitySuggestionItem}
            onMouseDown={() => handleSelect('', '')}
            style={{ opacity: 0.6 }}
          >
            {t('stepper.communityNone')}
          </div>
        </div>
      ) : null}
    </div>
  );
}
